import React, { useState } from "react";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import themeCreator from "@Themes/themeCreator";
import PageTitle from "@Components/PageTitle/PageTitle";
import { Container } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import DateSelector from "@Components/DateTimePickers/DatePicker";
import useSendPostRequest from "@Hooks/useSendPostRequest";
import {
  INVOICES_URL,
  GENERATE_TEMPLATE,
  UPLOAD_PAYMENT_EXCEL_FILE,
} from "../../API/APIRoutes";
import FormControlLabel from "@mui/material/FormControlLabel";
import DropDown from "../../Components/DropDown/DropDown";
import { useNavigate } from "react-router-dom";
import {
  OptionalDropDownValidation,
  NameAndNumberValidation,
  pastOrNowDateValidation,
  NowOrLaterDateValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import {
  paymentTypes,
  Currency,
  TransactionTypes,
  InvoiceStates,
} from "../../Utils/HelpingObjects/HelpingObjects";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Joi from "joi";
import Checkbox from "@mui/material/Checkbox";
import axios from "axios";
import * as DefaultRoutes from "@Routes/DefaultRoutes";

import { formatDateUTC } from "../../Utils/Validations/HelperFunctions/FormaterFunctions";
import { FormStyle } from "../Styles/FormStyle";
import { sub } from "date-fns";
import { Search } from "@mui/icons-material";
import SearchableDropDown from "../../Components/DropDown/SearchableDropDown";
import axiosInstance from "@API/axios";
import UploadFileButton from "../../Components/Buttons/uploadFileButton";
import { decryptId, encryptId } from "../../Utils/SecurityFunctions";

export default function OrderInvoice() {
  const [searchParams] = useSearchParams();
  const orderId = decryptId(searchParams.get("orderId"));
  const poNumber = decryptId(searchParams.get("poNumber"));
  const [isDownloading, setIsDownloading] = useState(false);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const theme = themeCreator();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialInvoice = {
    transactionNumber: "",
    transactionType: "Select Transaction Type",
    releasedDate: null,
    submissionDate: null,
    dueDate: null,
    state: "Select State",
    comment: "",
    poNumber: orderId ? poNumber : "",
    isReal: orderId ? true : false,
    coraId: "",
    payments: [],
    orderId: orderId,
    isFileAttached: false,
    totalInvoiceCost: "",
    currency: "Select Currency",
  };
  const validationSchema = Joi.object({
    transactionNumber: NameAndNumberValidation(
      "transactionNumber",
      "Transaction Number"
    ),
    releasedDate: Joi.alternatives()
      .try(pastOrNowDateValidation, Joi.allow(null))
      .optional(),
    submissionDate: pastOrNowDateValidation,
    transactionType: OptionalDropDownValidation("Transaction Type"),
    state: OptionalDropDownValidation("State"),
    poNumber: Joi.string().required().messages({
      "string.empty": "PoNumber is required",
      "any.required": "PoNumber is required",
    }),
    coraId: Joi.string().allow("").optional(),
    comment: Joi.string().allow("").optional(),
    dueDate: Joi.alternatives()
      .try(NowOrLaterDateValidation, Joi.allow(null))
      .optional(),
    isFileAttached: Joi.boolean(),
    isReal: Joi.boolean(),
    totalInvoiceCost: Joi.number().required(),
    currency: OptionalDropDownValidation("Currency"),
    orderId: Joi.alternatives()
      .try(Joi.string(), Joi.number(), Joi.allow(null))
      .optional(),
    payments: Joi.array().items(
      Joi.object({
        paymentName: Joi.string().required().messages({
          "string.empty": "Payment Name is required",
          "any.required": "Payment Name is required",
        }),
        paymentType: OptionalDropDownValidation("Payment Type"),
        amount: Joi.number().required().messages({
          "number.base": "Amount must be a number",
          "number.empty": "Amount is required",
          "any.required": "Amount is required",
        }),
        currency: OptionalDropDownValidation("Currency"),
        date: pastOrNowDateValidation,
      })
    ),
  });
  const [invoice, setInvoice] = useState(initialInvoice);
  const [expanded, setExpanded] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({
    transactionNumber: "",
    releasedDate: "",
    submissionDate: "",
    transactionType: "",
    poNumber: "",
    coraId: "",
    state: "",
    comment: "",
    dueDate: "",
    payments: [],
    general: "",
    totalInvoiceCost: "",
    currency: "",
  });
  const { isLoading: isloading, requestMethod: postData } =
    useSendPostRequest();

  let errorMessageForFilesUploads = "Only PDF files are allowed.";
  const onDatePick = (newValue) => {
    setInvoice({ ...invoice, releasedDate: newValue });
    setErrors({ ...errors, releasedDate: "" });
  };
  const onSubmissionDatePick = (newValue) => {
    setInvoice({ ...invoice, submissionDate: newValue });
    setErrors({ ...errors, submissionDate: "" });
  };
  const onDueDatePick = (newValue) => {
    setInvoice({ ...invoice, dueDate: newValue });
    setErrors({ ...errors, dueDate: "" });
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setInvoiceFile(file);
      setFileName(file.name);
    }
  };
  // Add these functions to handle payment operations
  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };
  const addNewPayment = () => {
    const newPayment = {
      paymentName: "",
      paymentType: "Select Payment Type",
      amount: "",
      currency: "Select Currency",
      date: null,
    };

    setInvoice({
      ...invoice,
      payments: [...invoice.payments, newPayment],
    });
    setErrors({
      ...errors,
      general: "",
    });
  };
  const removePayment = (index) => {
    const updatedPayments = [...invoice.payments];
    updatedPayments.splice(index, 1);

    setInvoice({
      ...invoice,
      payments: updatedPayments,
    });
  };
  const updatePayment = (index, field, value) => {
    const updatedPayments = [...invoice.payments];
    updatedPayments[index] = {
      ...updatedPayments[index],
      [field]: value,
    };

    setInvoice({
      ...invoice,
      payments: updatedPayments,
    });
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (!newErrors.payments[index]) {
        newErrors.payments[index] = {};
      }
      newErrors.payments[index][field] = ""; // Clear the error for the updated field
      return newErrors;
    });
  };
  const clearData = () => {
    setInvoice(initialInvoice);
    setExpanded(false);
    setInvoiceFile(null);
    setFileName("");
  };

  // Import payments functions
  const openImportPopup = () => setShowImportPopup(true);
  const closeImportPopup = () => {
    setShowImportPopup(false);
    setImportFile(null);
    setImportFileName("");
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handleFileUpload = async () => {
    if (!importFile) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await axiosInstance.post(
        `${UPLOAD_PAYMENT_EXCEL_FILE}/${orderId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      dispatch(
        setSnackbar({
          open: true,
          message: "Payments imported successfully",
          error: false,
        })
      );
      closeImportPopup();
      navigate(DefaultRoutes.pendingOrders);

      // }
    } catch (error) {
      console.error("File upload failed:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to import payments",
          error: true,
        })
      );
    } finally {
      setIsUploading(false);
    }
  };
  const downloadCSV = (id) => {
    setIsDownloading(true);

    // Determine the export URL based on the order state
    let exportUrl = GENERATE_TEMPLATE;
    const request = {
      transactionNumber: invoice.transactionNumber,
      transactionType: invoice.transactionType,
      releasedDate: invoice.releasedDate,
      submissionDate: invoice.submissionDate,
      coraId: invoice.coraId,
      state: invoice.state,
      comment: invoice.comment,
      dueDate: invoice.dueDate,
      totalInvoiceCost: invoice.totalInvoiceCost,
      currency: invoice.currency,
    };
    return axiosInstance
      .post(exportUrl, request, { responseType: "blob" })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.setAttribute("download", "payment-template.xlsx");
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
        // Set downloading state to false to hide snackbar
        setIsDownloading(false);
      })
      .catch((error) => {
        console.error("Excel download failed:", error);
        // Set downloading state to false in case of error
        setIsDownloading(false);
      });
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault(); // Add this to prevent form submission

    // Format all dates in the invoice and payments
    const formattedInvoice = {
      ...invoice,
      releasedDate: formatDateUTC(invoice.releasedDate),
      submissionDate: formatDateUTC(invoice.submissionDate),
      dueDate: formatDateUTC(invoice.dueDate),
      // Also format the payment dates
      payments: invoice.payments.map((payment) => ({
        ...payment,
        date: formatDateUTC(payment.date),
      })),
      isFileAttached: invoiceFile ? true : false,
    };

    const { error } = validationSchema.validate(formattedInvoice, {
      abortEarly: false,
    });
    if (error) {
      const newErrors = {
        transactionNumber: "",
        releasedDate: "",
        submissionDate: "",
        transactionType: "",
        poNumber: "",
        coraId: "",
        state: "",
        comment: "",
        dueDate: "",
        payments: Array(invoice.payments.length).fill({}),
        general: "",
        totalInvoiceCost: "",
        currency: "",
      };

      // Process all validation errors
      error.details.forEach((err) => {
        const path = err.path;

        if (path[0] === "payments" && path.length === 3) {
          // Payment-specific error (e.g., payments[0].amount)
          const paymentIndex = path[1];
          const field = path[2];

          if (!newErrors.payments[paymentIndex]) {
            newErrors.payments[paymentIndex] = {};
          }

          newErrors.payments[paymentIndex][field] = err.message;
        } else if (path[0] === "payments") {
          // Array-level error (e.g., min items)
          newErrors.general = err.message;
        } else {
          // Top-level error
          newErrors[path[0]] = err.message;
        }
      });

      setErrors(newErrors);
      return;
    }

    const response = await postData(INVOICES_URL, formattedInvoice);
    if (response) {
      if (!response.data.preSignedUrl && invoiceFile) {
        throw new Error("Presigned URL not received");
      }
      const { preSignedUrl, objectKey } = response.data;

      // Upload file to Minio
      if (invoiceFile) {
        await axios.put(preSignedUrl, invoiceFile, {
          headers: {
            "Content-Type": "application/pdf",
          },
        });
      }
      // show snackbar
      dispatch(
        setSnackbar({
          open: true,
          message: "Invoice submitted successfully",
          error: false,
        })
      );
      // Redirect to the pending orders page
      if (orderId) {
        sessionStorage.setItem("openSectionIndex", 0);
        navigate(DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId)));
      } else {
        navigate(DefaultRoutes.payments);
      }
    } else {
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to submit invoice",
          error: true,
        })
      );
    }
    // Clear the form data
    clearData();
  };
  // Then modify your form to include the payments section
  const form = (
    <>
      <form onSubmit={handleOnSubmit} className="formWrapper">
        <Grid
          className="inputContainer"
          container
          spacing={2}
          justifyContent="flex-end"
        >
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Po Number
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Po Number"
              value={invoice.poNumber ?? ""}
              disabled={orderId ? true : false}
              onChange={(e) => {
                const value = e.target.value;
                setInvoice({
                  ...invoice,
                  poNumber: value,
                });
                setErrors({ ...errors, poNumber: "" });
              }}
              helperText={
                <Typography variant="body2" marginTop={1} color={"error"}>
                  {errors.poNumber || ""}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Transaction Type
            </Typography>
            <SearchableDropDown
              state={invoice.transactionType}
              setState={(value) => {
                setInvoice({ ...invoice, transactionType: value });
                setErrors({ ...errors, transactionType: "" });
              }}
              name="Transaction Type"
              label=""
              items={TransactionTypes}
              placeholder="Select Transaction Type"
              helperText={errors.transactionType}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Transaction Number
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Transaction Number"
              value={invoice.transactionNumber ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setInvoice({
                  ...invoice,
                  transactionNumber: value,
                });
                setErrors({ ...errors, transactionNumber: "" });
              }}
              helperText={
                <Typography variant="body2" marginTop={1} color={"error"}>
                  {errors.transactionNumber || ""}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Cora Id
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Cora Id"
              value={invoice.coraId ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setInvoice({
                  ...invoice,
                  coraId: value,
                });
                setErrors({ ...errors, coraId: "" });
              }}
              helperText={
                <Typography variant="body2" marginTop={1} color={"error"}>
                  {errors.coraId || ""}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <DateSelector
              name={"Released Date"}
              value={invoice.releasedDate}
              error={errors.releasedDate}
              onPick={onDatePick}
            />
          </Grid>
          <Grid item xs={6}>
            <DateSelector
              name={"Submission Date"}
              value={invoice.submissionDate}
              error={errors.submissionDate}
              onPick={onSubmissionDatePick}
            />
          </Grid>
          <Grid item xs={6}>
            <DateSelector
              name={"Due Date"}
              value={invoice.dueDate}
              error={errors.dueDate}
              onPick={onDueDatePick}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Total Cost
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Total Cost"
              value={invoice.totalInvoiceCost ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setInvoice({
                  ...invoice,
                  totalInvoiceCost: value,
                });
                setErrors({ ...errors, totalInvoiceCost: "" });
              }}
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              helperText={
                <Typography variant="body2" marginTop={1} color={"error"}>
                  {errors.totalInvoiceCost || ""}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Currency
            </Typography>
            <DropDown
              state={invoice.currency}
              setState={(value) => {
                setInvoice({ ...invoice, currency: value });
                setErrors({ ...errors, currency: "" });
              }}
              name="currency"
              label=""
              items={Currency}
              helperText={errors.currency}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              State
            </Typography>
            <SearchableDropDown
              state={invoice.state}
              setState={(value) => {
                setInvoice({ ...invoice, state: value });
                setErrors({ ...errors, state: "" });
              }}
              name="State"
              label=""
              items={InvoiceStates}
              placeholder="Select State"
              helperText={errors.state}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }} align="left">
              Comment
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Comment"
              value={invoice.comment ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setInvoice({
                  ...invoice,
                  comment: value,
                });
                setErrors({ ...errors, comment: "" });
              }}
              helperText={
                <Typography variant="body2" marginTop={1} color={"error"}>
                  {errors.comment || ""}
                </Typography>
              }
            />
          </Grid>
          {/* Payments Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Accordion
              expanded={expanded}
              onChange={handleAccordionChange}
              sx={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: "8px",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: expanded ? "8px 8px 0 0" : "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PaymentIcon sx={{ mr: 1 }} />
                  Payments ({invoice.payments.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, pt: 0 }}>
                {invoice.payments.map((payment, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      position: "relative",
                    }}
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        color: theme.palette.error.main,
                      }}
                      onClick={() => removePayment(index)}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>

                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Payment #{index + 1}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1" sx={{ mb: 1 }} align="left">
                          Payment Name
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Payment Name"
                          value={payment.paymentName}
                          onChange={(e) =>
                            updatePayment(index, "paymentName", e.target.value)
                          }
                          type="text"
                          error={!!errors.payments[index]?.paymentName}
                          helperText={
                            <Typography
                              variant="body2"
                              marginTop={1}
                              color={"error"}
                            >
                              {errors.payments[index]?.paymentName || ""}
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ mb: 1 }} align="left">
                          Payment Type
                        </Typography>
                        <DropDown
                          state={payment.paymentType}
                          setState={(value) => {
                            updatePayment(index, "paymentType", value);
                          }}
                          name="payment Type"
                          label=""
                          items={paymentTypes}
                          placeholder="Select Payment Type"
                          helperText={errors.payments[index]?.paymentType}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <DateSelector
                          name={"Payment Date"}
                          value={payment.date}
                          onPick={(date) => updatePayment(index, "date", date)}
                          size="small"
                          error={errors.payments[index]?.date}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ mb: 1 }} align="left">
                          Amount
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Amount"
                          value={payment.amount}
                          onChange={(e) =>
                            updatePayment(index, "amount", e.target.value)
                          }
                          type="number"
                          inputProps={{ min: 0, step: "0.01" }}
                          error={!!errors.payments[index]?.amount}
                          helperText={
                            <Typography
                              variant="body2"
                              marginTop={1}
                              color={"error"}
                            >
                              {errors.payments[index]?.amount || ""}
                            </Typography>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" sx={{ mb: 1 }} align="left">
                          Currency
                        </Typography>
                        <DropDown
                          state={payment.currency}
                          setState={(value) => {
                            updatePayment(index, "currency", value);
                          }}
                          name="currency"
                          label=""
                          items={Currency}
                          placeholder="Select Currency"
                          helperText={errors.payments[index]?.currency}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addNewPayment}
                  >
                    Add New Payment
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
            {errors.general && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.general}
              </Typography>
            )}
          </Grid>
          <Box
            className="submissionBox"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <UploadFileButton
                handleFileChange={handleFileChange}
                fileName={fileName}
                acceptedTypes={["application/pdf"]}
                errorMessage="Only PDF files are allowed."
                buttonLabel={
                  invoiceFile
                    ? "Change File"
                    : orderId
                    ? "Upload Invoice"
                    : "Upload Transaction"
                }
                buttonVariant="outlined"
                buttonStartIcon={
                  invoiceFile ? <AttachFileIcon /> : <CloudUploadIcon />
                }
                dragAndDrop={true}
                fullWidth={false}
                sx={{
                  backgroundColor: invoiceFile
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                }}
              />

              <Button
                type="submit"
                variant="contained"
                className="submissionButton"
                size="large"
                disabled={isloading}
              >
                {!isloading ? (
                  orderId ? (
                    "Add Invoice"
                  ) : (
                    "Add Transaction"
                  )
                ) : (
                  <LoadingComponent size="2em" thickness={5} color="white" />
                )}
              </Button>

              <Button
                type="button"
                variant="contained"
                className="submissionButton"
                size="large"
                disabled={
                  isDownloading ||
                  !invoice.transactionNumber ||
                  !invoice.transactionType ||
                  invoice.transactionType === "Select Transaction Type" ||
                  !invoice.submissionDate ||
                  !invoice.state ||
                  invoice.state === "Select State"
                }
                onClick={downloadCSV}
              >
                {!isDownloading ? (
                  "Generate Excel Sheet Template"
                ) : (
                  <LoadingComponent size="2em" thickness={5} color="white" />
                )}
              </Button>

              <Button
                type="button"
                variant="contained"
                className="submissionButton"
                size="large"
                disabled={isUploading}
                onClick={openImportPopup}
              >
                {!isUploading ? (
                  "Import Payments"
                ) : (
                  <LoadingComponent size="2em" thickness={5} color="white" />
                )}
              </Button>
            </Box>

            {fileName && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                <AttachFileIcon
                  fontSize="small"
                  color="primary"
                  sx={{ mr: 0.5 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </form>
    </>
  );

  // Import Payments Popup
  const ImportPaymentsPopup = () => (
    <Dialog
      open={showImportPopup}
      onClose={closeImportPopup}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          textAlign: "center",
          fontWeight: 600,
          py: 2,
        }}
      >
        Import Payments from Excel
      </DialogTitle>
      <DialogContent sx={{ p: 3, mt: 2 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Upload an Excel file (.xlsx) containing payment data
          </Typography>

          <Button
            component="label"
            variant="outlined"
            startIcon={importFile ? <AttachFileIcon /> : <CloudUploadIcon />}
            sx={{
              mb: 2,
              minHeight: 56,
              backgroundColor: importFile
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
              borderStyle: "dashed",
              borderWidth: 2,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
            fullWidth
          >
            {importFile ? "Change File" : "Select Excel File"}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.numbers"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                // Validate file type
                const validTypes = [
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "application/vnd.ms-excel",
                  "application/vnd.apple.numbers",
                ];

                if (!validTypes.includes(file.type)) {
                  dispatch(
                    setSnackbar({
                      open: true,
                      message: "Only Excel files (.xlsx, .xls) are allowed.",
                      error: true,
                    })
                  );
                  e.target.value = null;
                  return;
                }

                handleImportFileChange(e);
              }}
            />
          </Button>

          {importFileName && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: theme.palette.success.light + "20",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${theme.palette.success.light}`,
              }}
            >
              <AttachFileIcon fontSize="small" color="success" sx={{ mr: 1 }} />
              <Typography variant="body2" color="success.main">
                {importFileName}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={closeImportPopup}
          variant="outlined"
          color="primary"
          size="large"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleFileUpload}
          variant="contained"
          color="primary"
          size="large"
          disabled={!importFile || isUploading}
          sx={{ minWidth: 100 }}
        >
          {isUploading ? (
            <LoadingComponent size="1.5em" thickness={5} color="white" />
          ) : (
            "Import"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <FormStyle className="PageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title={orderId ? "New Invoice" : "New Transaction"}
            PageIcon={<PaymentIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          ></PageTitle>
        </Box>
        <Box sx={{ pt: 3 }}>{form}</Box>
        <ImportPaymentsPopup />
      </Container>
    </FormStyle>
  );
}
